/**
 * Factory for idle-gated "falling" ambient effects (snow, petals, leaves, …).
 *
 * Returns a fixed full-screen canvas component with all the shared gating baked
 * in: fades in after an idle period (or a live preview), clears on input,
 * disabled under reduced-motion, `pointer-events: none`. Each effect supplies
 * its particle config, a sprite-draw function, and a fill (a color string, or a
 * function resolved at mount — e.g. reading a theme var off the canvas).
 */
import React, { useEffect, useRef } from 'react';

import { Z } from '@shared/constants/zIndex';
import { useMediaQuery } from '@shared/hooks/useMediaQuery';

import { createFallingParticles, drawFallingParticles, type FallingConfig } from './fallingParticles';
import { useIsIdle } from '../hooks/useIsIdle';

const DEFAULT_IDLE_MS = 15000;

interface FallingEffectOptions {
  config: FallingConfig;
  draw: (ctx: CanvasRenderingContext2D, size: number) => void;
  /**
   * Fill color(s): a single color, a palette (each particle picks one), or a
   * function resolved at mount (e.g. read theme var(s) off the canvas).
   */
  fill: string | string[] | ((canvas: HTMLCanvasElement) => string | string[]);
  idleMs?: number;
  testId?: string;
}

/** Build an idle-gated falling-particle effect component. */
export function createFallingEffect(opts: FallingEffectOptions): React.FC {
  const { config, draw, fill, idleMs = DEFAULT_IDLE_MS, testId } = opts;

  const FallingEffect: React.FC = () => {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const idle = useIsIdle(idleMs, !reducedMotion);
    const active = !reducedMotion && idle;
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
      if (!active) return undefined;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return undefined;

      const resolved = typeof fill === 'function' ? fill(canvas) : fill;
      const fills = Array.isArray(resolved) ? resolved : [resolved];
      let width = (canvas.width = window.innerWidth);
      let height = (canvas.height = window.innerHeight);
      let particles = createFallingParticles(config, width, height, fills.length);
      let raf = 0;
      const render = () => {
        drawFallingParticles(ctx, particles, width, height, fills, draw);
        raf = requestAnimationFrame(render);
      };
      const onResize = () => {
        width = canvas.width = window.innerWidth;
        height = canvas.height = window.innerHeight;
        particles = createFallingParticles(config, width, height, fills.length);
      };

      window.addEventListener('resize', onResize);
      raf = requestAnimationFrame(render);
      canvas.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 1200, easing: 'ease-out' });

      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('resize', onResize);
      };
    }, [active]);

    if (!active) return null;

    return (
      <canvas
        ref={canvasRef}
        aria-hidden
        data-testid={testId}
        className="pointer-events-none fixed inset-0 h-full w-full"
        style={{ zIndex: Z.AMBIENT }}
      />
    );
  };

  FallingEffect.displayName = 'FallingEffect';
  return FallingEffect;
}
